<template>
  <th-popover
    :title="t('aichat.main.blocks.explain.title')"
    placement="top-end"
    :button-label="t('aichat.main.blocks.explain.title')"
    button-test-id="ai-chat-card-explain-button"
  >
    <div class="explain w-72">
      <p
        v-if="explain.tool_name || windowText"
        data-testid="ai-chat-card-explain-summary"
      >
        <template v-if="explain.tool_name">
          {{
            t('aichat.main.blocks.explain.answered_by', {
              tool: explain.tool_name
            })
          }}
        </template>
        {{ windowText }}
      </p>
      <p
        v-if="explain.effectiveRange"
        class="explain-muted"
        data-testid="ai-chat-card-explain-range"
      >
        {{
          t('aichat.main.blocks.explain.range', {
            start: formatInstant(explain.effectiveRange.start),
            end: formatInstant(explain.effectiveRange.end)
          })
        }}
      </p>
      <el-descriptions
        v-if="toolArgs.length"
        :column="1"
        size="small"
        border
        class="explain-args"
      >
        <el-descriptions-item v-for="arg in toolArgs" :key="arg.key">
          <template #label>
            <span data-testid="ai-chat-card-explain-arg-key">{{
              arg.key
            }}</span>
          </template>
          <span data-testid="ai-chat-card-explain-arg-value">
            {{ arg.value }}
          </span>
        </el-descriptions-item>
      </el-descriptions>
      <p
        v-if="explain.truncated"
        class="explain-muted"
        data-testid="ai-chat-card-explain-truncated"
      >
        {{ t('aichat.main.blocks.explain.truncated') }}
      </p>
    </div>
  </th-popover>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ThPopover from '@shared/components/popover/index.vue'
import type { CardExplain } from '../../types'

/* the analytics windows the tools group figures by; any other value gets no sentence instead of a raw enum */
const WINDOW_KEYS = {
  hourly: 'aichat.main.blocks.explain.windows.hourly',
  daily: 'aichat.main.blocks.explain.windows.daily',
  weekly: 'aichat.main.blocks.explain.windows.weekly',
  monthly: 'aichat.main.blocks.explain.windows.monthly',
  quarterly: 'aichat.main.blocks.explain.windows.quarterly',
  yearly: 'aichat.main.blocks.explain.windows.yearly'
} as const

type KnownWindow = keyof typeof WINDOW_KEYS

/* the card's "how this was calculated" affordance: which tool answered, with which filters, over which range */
const props = defineProps<{
  explain: CardExplain
}>()

const { t } = useI18n()

const isKnownWindow = (window?: string): window is KnownWindow =>
  window !== undefined && Object.hasOwn(WINDOW_KEYS, window)

const windowText = computed(() =>
  isKnownWindow(props.explain.window)
    ? t(WINDOW_KEYS[props.explain.window])
    : ''
)

const toolArgs = computed(() =>
  Object.entries(props.explain.tool_args || {}).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value)
  }))
)

/* '2026-10-07T12:00:00.000Z' → '2026-10-07 12:00' */
const formatInstant = (instant: string): string =>
  instant.slice(0, 16).replace('T', ' ')
</script>

<style scoped>
.explain-button {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.25rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  color: var(--el-text-color-secondary);
  cursor: pointer;
}

.explain-button:hover {
  background: var(--el-fill-color);
  color: var(--primary-color);
}

.explain-button svg {
  width: 0.875rem;
  height: 0.875rem;
}

.explain p {
  margin: 0 0 0.5rem;
  color: var(--el-text-color-primary);
  font-size: 0.75rem;
  line-height: 1.6;
}

.explain-title {
  margin-bottom: 0.375rem;
  color: var(--el-text-color-primary);
  font-size: 0.78125rem;
  font-weight: 700;
}

.explain p.explain-muted {
  color: var(--el-text-color-secondary);
}

.explain-args {
  margin-bottom: 0.5rem;
}
</style>
